// Global Instructions Rule Applied!
//
// Consolidated-fund Dashboard tab: the three-level committed-capital view
// (consolidated / per member pocket) with called / uncalled / paid /
// outstanding at each level. KPI tiles mirror the Capital Call Position
// screen's tile shape; the member breakdown is a compact AntD table.

import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Card, Col, Progress, Row, Spin, Table, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../state_manager/store.ts'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  FundGroupDashboard as FundGroupDashboardData,
  FundGroupMemberStat
} from '../../../state_manager/data_models/fund_group.ts'
import { fetchFundGroupDashboard } from '../services/fundGroupsService.ts'
import { AgentSignalsSection } from '../../internal_agents/components/AgentSignalsSection.tsx'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { outsideCommitmentHelp } from '../../capital_calls/utils/commitmentTerms.ts'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'

const { Text } = Typography

interface Props {
  fundGroupId: string | null
}

export function FundGroupDashboard({ fundGroupId }: Props): React.ReactElement | null {
  const [dashboard, setDashboard] = useState<FundGroupDashboardData | null>(null)
  const [loading, setLoading] = useState(false)
  const groupName = useSelector(
    (s: RootState) => (s.globalConfigs?.fund_groups ?? []).find((g) => g.id === fundGroupId)?.name ?? 'Consolidated fund'
  )

  const load = useCallback(async () => {
    if (!fundGroupId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundGroupDashboard(fundGroupId, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to load consolidated fund')
        return
      }
      setDashboard(res.data)
    } finally {
      setLoading(false)
    }
  }, [fundGroupId])

  useEffect(() => {
    void load()
  }, [load])

  if (!fundGroupId) return null

  // fees / expenses the PRIMARY fund's terms keep outside the commitment do
  // not draw it down (spec A3.9 addendum part 4): the consolidated Uncalled
  // tile says which line types are left out; null = everything counts
  const outsideHelp = outsideCommitmentHelp(
    dashboard?.prog__commitment_includes_mgmt_fees,
    dashboard?.prog__commitment_includes_expenses
  )

  const tiles = dashboard
    ? [
        { label: 'Committed (consolidated)', value: dashboard.prog__committed_fmt ?? '-' },
        { label: 'Closed capital', value: dashboard.prog__closed_capital_fmt ?? '-' },
        {
          label: 'Called to date',
          value: dashboard.prog__called_to_date_fmt ?? '-',
          sub: `${(((dashboard.prog__pct_drawn ?? 0) * 1000) / 10).toFixed(1)}% drawn`
        },
        { label: 'Uncalled', value: dashboard.prog__uncalled_fmt ?? '-', help: outsideHelp },
        { label: 'Paid', value: dashboard.prog__paid_fmt ?? '-' },
        {
          label: 'Outstanding',
          value: dashboard.prog__outstanding_fmt ?? '-',
          danger: Math.abs(dashboard.prog__outstanding ?? 0) >= 0.005
        },
        { label: 'Investors', value: String(dashboard.prog__investor_count ?? 0) },
        { label: 'Consolidated Commitments', value: String(dashboard.prog__subscription_count ?? 0) }
      ]
    : []

  const memberColumns = [
    {
      title: 'Fund',
      key: 'fund',
      render: (_: unknown, m: FundGroupMemberStat) => (
        <span>
          {m.funds__name ?? '—'}
          {m.fund_group_members__is_primary ? <Text type='secondary'> (primary)</Text> : null}
        </span>
      )
    },
    {
      title: 'Committed (pocket)',
      key: 'committed',
      align: 'right' as const,
      render: (_: unknown, m: FundGroupMemberStat) => m.prog__committed_fmt ?? '-'
    },
    {
      title: 'Closed',
      key: 'closed',
      align: 'right' as const,
      // effective closed capital today: the primary carries the gross while a
      // satellite allocation is pending (SBA approval); the satellite counts
      // its allocation only once closed
      render: (_: unknown, m: FundGroupMemberStat) => {
        const pending = m.prog__pending_allocation ?? 0
        return (
          <span>
            {m.prog__closed_capital_fmt ?? '-'}
            {pending > 0.005 ? (
              <div className='text-xs text-amber-700'>
                {m.fund_group_members__is_primary
                  ? `includes ${m.prog__pending_allocation_fmt ?? ''} pending satellite approval`
                  : `${m.prog__pending_allocation_fmt ?? ''} allocated, not yet closed`}
              </div>
            ) : null}
          </span>
        )
      }
    },
    {
      title: 'Called to date',
      key: 'called',
      align: 'right' as const,
      render: (_: unknown, m: FundGroupMemberStat) => m.prog__called_to_date_fmt ?? '-'
    },
    {
      title: '% drawn',
      key: 'pct',
      width: 140,
      render: (_: unknown, m: FundGroupMemberStat) => (
        <Progress
          percent={Math.round((m.prog__pct_drawn ?? 0) * 1000) / 10}
          size='small'
          format={(p) => `${(p ?? 0).toFixed(1)}%`}
        />
      )
    },
    {
      title: 'Uncalled',
      key: 'uncalled',
      align: 'right' as const,
      render: (_: unknown, m: FundGroupMemberStat) => m.prog__uncalled_fmt ?? '-'
    },
    {
      title: 'Paid',
      key: 'paid',
      align: 'right' as const,
      render: (_: unknown, m: FundGroupMemberStat) => m.prog__paid_fmt ?? '-'
    },
    {
      title: 'Outstanding',
      key: 'outstanding',
      align: 'right' as const,
      render: (_: unknown, m: FundGroupMemberStat) => {
        const v = m.prog__outstanding ?? 0
        if (Math.abs(v) < 0.005) return <Text type='secondary'>—</Text>
        return <Text type='danger'>{m.prog__outstanding_fmt ?? '-'}</Text>
      }
    }
  ]

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center justify-end'>
        <Button icon={<ReloadOutlined />} size='small' onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </div>

      {/* The consolidated-fund tracker's open signals (LP agent programme
          Phase 5: highlights_ready when a period highlights draft awaits
          review) — the fund Dashboard's composition. */}
      <AgentSignalsSection
        contextTable='fund_groups'
        contextId={fundGroupId}
        openOnly
        maxVisible={3}
        hideWhenEmpty
        collapseKey='fund_group_dashboard_signals'
      />

      {loading && !dashboard ? (
        <div className='flex justify-center p-6'>
          <Spin />
        </div>
      ) : (
        <>
          <Row gutter={[12, 12]}>
            {tiles.map((tile) => (
              <Col xs={12} md={6} key={tile.label}>
                <Card size='small'>
                  <div className='text-xs text-gray-500'>
                    {tile.label}
                    <HelpTip title={tile.help} />
                  </div>
                  <div className='text-lg font-medium'>
                    {tile.danger ? <Text type='danger'>{tile.value}</Text> : tile.value}
                  </div>
                  {tile.sub ? <div className='text-xs text-gray-500'>{tile.sub}</div> : null}
                </Card>
              </Col>
            ))}
          </Row>

          <Card
            size='small'
            title='Member funds (pockets)'
            extra={
              <ExportRowsButton
                name={`${groupName} Member funds`}
                rows={dashboard?.prog__members ?? []}
                columns={[
                  { title: 'Fund', value: (m) => m.funds__name },
                  { title: 'Primary', value: (m) => (m.fund_group_members__is_primary ? 'Yes' : null) },
                  { title: 'Committed (pocket)', value: (m) => m.prog__committed },
                  { title: 'Closed', value: (m) => m.prog__closed_capital },
                  // the Closed sub-line: on the primary, gross pending satellite approval;
                  // on a satellite, allocated but not yet closed
                  { title: 'Pending allocation', value: (m) => m.prog__pending_allocation },
                  { title: 'Called to date', value: (m) => m.prog__called_to_date },
                  { title: '% drawn', value: (m) => m.prog__pct_drawn, kind: 'percent' },
                  { title: 'Uncalled', value: (m) => m.prog__uncalled },
                  { title: 'Paid', value: (m) => m.prog__paid },
                  { title: 'Outstanding', value: (m) => m.prog__outstanding },
                  // every pocket is in the consolidated fund's currency
                  { title: 'Currency', value: () => dashboard?.currency_code }
                ]}
              />
            }
          >
            <Table
              size='small'
              pagination={false}
              rowKey={(m: FundGroupMemberStat) => String(m.funds__id ?? m.funds__name ?? '')}
              dataSource={dashboard?.prog__members ?? []}
              columns={memberColumns}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}
    </div>
  )
}

export default FundGroupDashboard
